import { useEffect, useState } from "react";
import { listMovements, type WalletMovementFull } from "@/lib/api";
import { formatAmount, formatDate, formatRelative } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, type ColumnDef } from "@/components/DataTable";

const TYPES = [
  { value: "credit_payment", label: "Crédit paiement" },
  { value: "debit_refund", label: "Débit refund" },
  { value: "debit_settlement", label: "Débit settlement" },
  { value: "credit_settlement_failed", label: "Annulation settlement" },
  { value: "debit_commission", label: "Commission" },
  { value: "manual_adjustment", label: "Ajustement manuel" },
];

export function HistoryPage() {
  const [movements, setMovements] = useState<WalletMovementFull[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const params: any = { limit: 100 };
      if (!reset && cursor) params.cursor = cursor;
      if (type) params.movement_type = type;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const r = await listMovements(params);
      setMovements(reset ? r.data : [...movements, ...r.data]);
      setCursor(r.next_cursor ?? null);
      setHasMore(r.has_more);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCursor(null);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, from, to]);

  const columns: ColumnDef<WalletMovementFull>[] = [
    {
      key: "date",
      header: "Date",
      minWidth: 130,
      cell: (m) => <span className="text-xs text-[#3f4254]" title={formatDate(m.created_at)}>{formatRelative(m.created_at)}</span>,
    },
    {
      key: "wallet",
      header: "Wallet",
      minWidth: 160,
      cell: (m) => (
        <div>
          <div className="text-sm font-semibold">{m.wallet_type === "mobile_money" ? "Mobile Money" : "Cartes"}</div>
          <div className="text-xs text-[#7e8299]">{m.wallet_currency}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      minWidth: 170,
      cell: (m) => <Badge color={movementColor(m.type)}>{labelType(m.type)}</Badge>,
    },
    {
      key: "ref",
      header: "Référence",
      minWidth: 220,
      wrap: true,
      cell: (m) => (
        <div>
          <div className="text-xs text-[#7e8299] capitalize">{m.reference_type}</div>
          <div className="font-mono text-xs text-[#181c32]">{m.reference_id}</div>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Montant",
      align: "right",
      minWidth: 130,
      cell: (m) => (
        <span className={`font-bold ${m.amount_minor >= 0 ? "text-success-700" : "text-danger-700"}`}>
          {m.amount_minor >= 0 ? "+" : ""}
          {formatAmount(m.amount_minor, m.currency)}
        </span>
      ),
    },
    {
      key: "balance",
      header: "Solde après",
      align: "right",
      minWidth: 130,
      cell: (m) => <span className="text-sm text-[#181c32]">{formatAmount(m.balance_after_minor, m.currency)}</span>,
    },
    {
      key: "desc",
      header: "Description",
      minWidth: 240,
      wrap: true,
      cell: (m) => <span className="text-xs text-[#3f4254]">{m.description || "—"}</span>,
    },
  ];

  const filters = (
    <>
      <select value={type} onChange={(e) => setType(e.target.value)} className="form-input py-2 px-3 text-sm w-auto">
        <option value="">Tous types</option>
        {TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
      <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="form-input py-2 px-3 text-sm w-auto" placeholder="Du" />
      <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="form-input py-2 px-3 text-sm w-auto" placeholder="Au" />
      {(from || to || type) && (
        <button onClick={() => { setType(""); setFrom(""); setTo(""); }} className="btn btn-light btn-sm">
          Réinitialiser
        </button>
      )}
    </>
  );

  return (
    <div>
      <PageHeader title="Historique" breadcrumbs={[{ label: "Finance" }, { label: "Historique" }]} />

      <DataTable<WalletMovementFull>
        title="Toutes les opérations"
        subtitle="Tous les mouvements de tous vos wallets"
        columns={columns}
        data={movements}
        rowKey={(m) => m.id}
        loading={loading && movements.length === 0}
        filtersSlot={filters}
        loadMore={hasMore ? { hasMore, onLoadMore: () => load(false), loading } : undefined}
      />
    </div>
  );
}

function labelType(t: string): string {
  return (
    {
      credit_payment: "Crédit paiement",
      debit_refund: "Débit remboursement",
      debit_settlement: "Débit settlement",
      credit_settlement_failed: "Annulation settlement",
      debit_commission: "Commission",
      manual_adjustment: "Ajustement manuel",
    } as Record<string, string>
  )[t] ?? t;
}

function movementColor(t: string): "success" | "danger" | "warning" | "info" | "light" {
  if (t.startsWith("credit_")) return "success";
  if (t === "debit_refund") return "danger";
  if (t === "debit_commission") return "warning";
  if (t === "debit_settlement") return "info";
  return "light";
}
